'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { ArrowRight } from '@/app/components/icons/ArrowRight';
import { cn, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useLocale, useRouter } from '@/i18n/routing';
import { useGetBannerList } from '../_hooks/use-get-banner-list';
import {
  EAGER_QUICK_ENTRY_IMAGE_COUNT,
  getHomeQuickEntries,
  HOME_PROMO_VIDEO_SRC,
  TOP_MODULE_STEP_SECONDS,
} from '../constants';

export const HomeToolsSection = () => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const { carouselBanners, getBannerList, getBannerListLoading } = useGetBannerList();
  const [hasRequestedBannerList, setHasRequestedBannerList] = useState(false);
  const [topModuleIndex, setTopModuleIndex] = useState(0);
  const [topModuleInteraction, setTopModuleInteraction] = useState(0);

  useEffect(() => {
    setHasRequestedBannerList(true);
    getBannerList();
  }, [getBannerList]);

  const isCarouselLoading = !hasRequestedBannerList || getBannerListLoading;
  const topModuleItems = carouselBanners;

  useEffect(() => {
    if (topModuleItems.length <= 1) return;
    const timer = setInterval(() => {
      setTopModuleIndex((prev) => (prev + 1) % topModuleItems.length);
    }, TOP_MODULE_STEP_SECONDS * 1000);
    return () => clearInterval(timer);
  }, [topModuleInteraction, topModuleItems.length]);

  const handleTopModuleGoto = (idx: number) => {
    setTopModuleIndex(idx);
    setTopModuleInteraction((prev) => prev + 1);
  };

  const handleTopModuleStep = (delta: number) => {
    setTopModuleIndex((prev) => (prev + delta + topModuleItems.length) % topModuleItems.length);
    setTopModuleInteraction((prev) => prev + 1);
  };

  const handleBannerClick = (index: number) => {
    const banner = topModuleItems[index];
    if (!banner) return;

    reportEvent('fixed_banner_click', {
      banner_id: banner.id || '',
      activity_id: banner.activity_id || '',
      banner_position: index + 1,
      banner_name: banner.title || '',
    });

    if (banner.type === 'activity' && banner.activity_id) {
      router.push(`/activity?id=${banner.activity_id}`);
    } else if (banner.redirect_url) {
      openOrRedirect(banner.redirect_url);
    }
  };

  const quickEntries = getHomeQuickEntries(t, locale);
  const [promoEntry] = quickEntries;

  return (
    <div className="mb-6 flex flex-col">
      {/* 移动端摊平成 contents，让 banner / 标题 / 卡片区直接排进外层的列，再用 order 换成 banner → 标题 → 卡片 */}
      <div className="flex items-stretch gap-4 pb-1 sm:contents">
        <div className="group/banner relative aspect-[477/169] h-[188px] flex-shrink-0 overflow-hidden rounded-16 text-white sm:order-1 sm:mb-4 sm:aspect-video sm:h-auto">
          {isCarouselLoading ? (
            <div className="size-full animate-pulse rounded-16 bg-system-white08" />
          ) : (
            topModuleItems.map((item, idx) => (
              <img
                key={`${item.id}-${item.cover_uri}-${idx}`}
                src={item.cover_uri}
                alt={item.title}
                loading={idx === 0 ? 'eager' : 'lazy'}
                decoding="async"
                onClick={() => handleBannerClick(idx)}
                className={cn(
                  'absolute inset-0 size-full cursor-pointer object-cover transition-opacity duration-700 ease-out',
                  idx === topModuleIndex ? 'pointer-events-auto' : 'pointer-events-none',
                )}
                style={{ opacity: idx === topModuleIndex ? 1 : 0 }}
              />
            ))
          )}
          {topModuleItems.length > 1 && (
            <>
              <button
                type="button"
                aria-label="previous"
                onClick={() => handleTopModuleStep(-1)}
                className="-translate-y-1/2 absolute top-1/2 left-3 z-20 flex size-8 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:bg-black/60 group-hover/banner:opacity-100"
              >
                <ArrowRight className="rotate-180 text-lg" />
              </button>
              <button
                type="button"
                aria-label="next"
                onClick={() => handleTopModuleStep(1)}
                className="-translate-y-1/2 absolute top-1/2 right-3 z-20 flex size-8 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:bg-black/60 group-hover/banner:opacity-100"
              >
                <ArrowRight className="text-lg" />
              </button>
            </>
          )}
          {topModuleItems.length > 1 && (
            <div className="-translate-x-1/2 absolute bottom-3 left-1/2 z-20 flex items-center gap-2">
              {topModuleItems.map((item, idx) => (
                <button
                  key={`${item.id}-${item.cover_uri}-${idx}`}
                  type="button"
                  aria-label={`go to ${idx + 1}`}
                  onClick={() => handleTopModuleGoto(idx)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${idx === topModuleIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/60'}`}
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 items-stretch gap-4 overflow-x-auto pb-1 sm:order-3 sm:flex-none">
          {promoEntry && (
            <button
              type="button"
              onClick={() => router.push(promoEntry.path)}
              className="group relative flex h-[188px] w-[350px] flex-shrink-0 overflow-hidden rounded-16 border border-white/[0.06] bg-[#17191c] text-left transition-colors hover:bg-[#1e2024] sm:hidden"
            >
              <div className="relative z-10 flex w-full flex-col justify-start gap-3 px-7 py-5">
                <svg
                  className="h-7 w-auto self-start text-white"
                  viewBox="0 0 862 280"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  role="img"
                  aria-label={promoEntry.title}
                >
                  <path
                    d="M71.3875 181.4L109.588 70.4H139.388L88.1875 210H54.1875L2.9875 70.4H32.9875L71.3875 181.4ZM156.175 210V99.2H184.175V210H156.175ZM170.375 86C160.375 86 153.175 78.8 153.175 69.6C153.175 60.4 160.375 53.2 170.375 53.2C180.175 53.2 187.575 60.4 187.575 69.6C187.575 78.8 180.175 86 170.375 86ZM204.639 154.2C204.639 119.6 227.039 97.4 255.439 97.4C270.039 97.4 284.039 104.2 291.639 114.6V62H320.039V210H291.639V193.6C285.039 203.8 272.639 211.8 255.239 211.8C227.039 211.8 204.639 188.8 204.639 154.2ZM291.839 154.6C291.839 133.6 277.839 122 262.439 122C247.439 122 233.239 133.2 233.239 154.2C233.239 175.2 247.439 187.2 262.439 187.2C277.839 187.2 291.839 175.6 291.839 154.6ZM451.986 99.2V210H423.786V196C416.786 205.4 404.586 211.4 390.786 211.4C364.986 211.4 346.386 194.6 346.386 164.2V99.2H374.386V160.2C374.386 178 384.186 187.6 398.986 187.6C414.186 187.6 423.786 178 423.786 160.2V99.2H451.986Z"
                    fill="currentColor"
                  />
                  <path
                    d="M850.741 29.5851C856.264 29.5851 860.118 34.0185 859.35 39.4875L839.727 179.115C834.192 218.493 797.471 250.415 757.706 250.415H506.706C501.183 250.415 497.329 245.981 498.098 240.512L517.722 100.884C523.256 61.5068 559.977 29.5854 599.741 29.5851H850.741ZM608.986 69.9845L584.096 91.0753L577.066 131.355L594.356 152.635H652.116L657.626 159.285L654.966 174.865L646.986 181.515H573.646L568.706 210.015H662.946L687.836 188.925L695.436 145.225L678.146 124.135H620.386L614.876 117.485L616.966 105.135L624.946 98.4845H696.386L701.326 69.9845H608.986ZM727.675 69.9845L722.735 98.2951H740.405L725.585 181.895H706.016L701.075 210.015H775.936L780.875 181.895H761.495L781.255 69.9845H727.675Z"
                    fill="currentColor"
                  />
                </svg>
                <div className="text-sm text-white/40">{promoEntry.description ?? t('home.promoDesc')}</div>
                <span className="mt-auto inline-flex w-fit items-center gap-1.5 rounded-full border border-white/25 px-4 py-2 font-medium text-sm text-white transition-colors group-hover:border-white/50 group-hover:bg-white/5">
                  {t('home.tryItNow')}
                </span>
              </div>
              <div className="absolute inset-y-0 right-0 z-0 w-1/2 overflow-hidden">
                <video
                  src={HOME_PROMO_VIDEO_SRC}
                  poster={promoEntry.image}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="size-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#17191c] to-transparent" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#1e2024] to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="pointer-events-none absolute inset-0 bg-[#1e2024] opacity-0 transition-opacity group-hover:opacity-30" />
              </div>
            </button>
          )}
          <div className="grid shrink-0 grid-flow-row grid-cols-[repeat(7,240px)] grid-rows-2 gap-3 pb-1 sm:grid-cols-[repeat(8,240px)]">
            {quickEntries.map((entry, index) => (
              <button
                key={entry.key}
                type="button"
                onClick={() => router.push(entry.path)}
                className={cn(
                  'group relative flex h-[88px] w-[240px] items-center gap-3 overflow-hidden rounded-16 border border-white/[0.06] bg-[#17191c] px-3 text-left transition-colors hover:bg-[#1e2024]',
                  // 首个条目桌面端由上面的 promo 大卡承载，只在移动端作为普通工具卡进网格
                  entry === promoEntry && 'hidden sm:flex',
                )}
              >
                <img
                  src={entry.image}
                  alt=""
                  width={56}
                  height={56}
                  loading={index < EAGER_QUICK_ENTRY_IMAGE_COUNT ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={index < EAGER_QUICK_ENTRY_IMAGE_COUNT ? 'high' : 'low'}
                  className="size-14 flex-shrink-0 rounded-12 object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-normal text-[15px] text-white leading-[1.4]">{entry.title}</span>
                    {entry.tag ? (
                      <span className="flex-shrink-0 rounded-full border border-system-white12 px-2 py-0.5 text-white/60 text-xs">
                        {entry.tag}
                      </span>
                    ) : null}
                  </div>
                </div>
                {entry.cornerBadge ? (
                  <span
                    className={`absolute top-0 right-0 rounded-bl-12 px-2.5 py-1 font-medium text-white text-xs ${entry.cornerBadge.tone === 'hot' ? 'bg-[#C6743A]' : 'bg-[#2F9E8F]'}`}
                  >
                    {entry.cornerBadge.label}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
